import React from 'react';
import { Helmet } from 'react-helmet-async';

export const defaultSite = {
  name: 'Attain Domains',
  baseUrl: typeof window !== 'undefined' ? window.location.origin : 'https://attaindomains.com',
  titleTemplate: '%s — Attain Domains',
  defaultTitle: 'Premium domains for startups',
  defaultDesc:
    'Buy premium, brandable domains with secure checkout through a trusted payment provider. I cover all escrow fees.',
  twitterCard: 'summary' as const,
};

export function withTemplate(title?: string) {
  if (!title) return defaultSite.defaultTitle;
  const t = String(title).trim();
  return t.includes('—') ? t : defaultSite.titleTemplate.replace('%s', t);
}

export function canonicalize(pathname = '') {
  const base = defaultSite.baseUrl?.replace(/\/+$/, '') || '';
  const path = String(pathname || '/');
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

interface OpenGraphProps {
  title?: string;
  description?: string;
  url?: string;
  type?: string;
  image?: string;
}

interface TwitterProps {
  card?: 'summary' | 'summary_large_image';
  title?: string;
  description?: string;
  image?: string;
}

interface SeoProps {
  title?: string;
  description?: string;
  canonical?: string;
  og?: OpenGraphProps;
  twitter?: TwitterProps;
  jsonLd?: any | any[];
}

export function Seo({ title, description, canonical, og, twitter, jsonLd }: SeoProps) {
  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      {og?.title && <meta property="og:title" content={og.title} />}
      {og?.description && <meta property="og:description" content={og.description} />}
      {og?.url && <meta property="og:url" content={og.url} />}
      {og?.type && <meta property="og:type" content={og.type} />}
      {og?.image && <meta property="og:image" content={og.image} />}

      {/* Twitter */}
      {twitter?.card && <meta name="twitter:card" content={twitter.card} />}
      {twitter?.title && <meta name="twitter:title" content={twitter.title} />}
      {twitter?.description && <meta name="twitter:description" content={twitter.description} />}
      {twitter?.image && <meta name="twitter:image" content={twitter.image} />}

      {/* JSON-LD */}
      {jsonLdArray.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </Helmet>
  );
}
